import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { POSTS } from "@/content/posts";

/** Extra panels shown only on English articles in the "AEO" category. */

const PILLAR_SLUG = "what-is-aeo-how-to-get-cited-by-chatgpt-2026";

/** Short anchor text for AEO cluster posts; other posts fall back to their title. */
const ANCHOR_TEXT: Record<string, string> = {
  "what-is-aeo-how-to-get-cited-by-chatgpt-2026":
    "What is AEO? How to Get Cited by ChatGPT in 2026",
  "schema-markup-for-aeo-2026": "Schema Markup for AEO",
  "llms-txt-explained-2026": "llms.txt Explained",
  "entity-seo-signals-for-aeo": "Entity SEO Signals for AEO",
  "measure-aeo-performance-2026": "How to Measure AEO Performance",
  "common-aeo-mistakes-to-avoid": "7 Common AEO Mistakes to Avoid",
};

/** Call to action for the AEO & GEO service. */
export function AeoServiceCta() {
  return (
    <div className="mt-16 rounded-3xl border border-primary/30 bg-card p-8 lg:p-10">
      <div className="text-xs uppercase tracking-[0.2em] text-primary">
        Done reading? Put it to work.
      </div>
      <h3 className="mt-3 font-display text-3xl">
        Want to be cited by ChatGPT, Perplexity &amp; Gemini?
      </h3>
      <p className="mt-4 text-muted-foreground">
        I run a dedicated AEO &amp; GEO program for brands serious about AI search visibility —
        entity SEO, schema, and citation-worthy content, shipped end-to-end.
      </p>
      <Link
        to="/services/aeo-geo"
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium"
      >
        See the AEO &amp; GEO service <ArrowUpRight size={16} />
      </Link>
    </div>
  );
}

/** Links to every other post in the AEO cluster, pointing non-pillar readers to the pillar first. */
export function AeoSeries({ currentSlug }: { currentSlug: string }) {
  const cluster = POSTS.filter((x) => x.category === "AEO" && x.slug !== currentSlug);
  if (cluster.length === 0) return null;
  return (
    <div className="mt-12 rounded-3xl border border-border bg-card/50 p-8 lg:p-10">
      <div className="text-xs uppercase tracking-[0.2em] text-primary">The AEO series</div>
      <h3 className="mt-3 font-display text-2xl">Continue reading the AEO cluster</h3>
      <p className="mt-3 text-sm text-muted-foreground">
        {currentSlug === PILLAR_SLUG ? (
          <>The supporting deep-dives that expand on this pillar.</>
        ) : (
          <>
            Start with the pillar:{" "}
            <Link
              to="/blog/$slug"
              params={{ slug: PILLAR_SLUG }}
              className="text-primary underline-offset-4 hover:underline"
            >
              {ANCHOR_TEXT[PILLAR_SLUG]}
            </Link>
            . Then keep going below.
          </>
        )}
      </p>
      <ul className="mt-6 grid sm:grid-cols-2 gap-3">
        {cluster.map((c) => (
          <li key={c.slug}>
            <Link
              to="/blog/$slug"
              params={{ slug: c.slug }}
              className="group flex items-start gap-3 rounded-2xl border border-border p-4 hover:border-primary/40 hover:bg-card transition"
            >
              <ArrowUpRight size={16} className="mt-1 text-primary shrink-0" />
              <span className="text-sm font-medium leading-snug">
                {ANCHOR_TEXT[c.slug] ?? c.title}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
